import React, { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { Plus, Pencil, Trash2, CheckCircle, XCircle, Clock, AlertCircle, FileQuestion, Image } from 'lucide-react';
import { useCompany } from '../../contexts/CompanyContext';
import { modules } from '../../lib/api';
import { cn } from '../../utils/cn';
import { QuizManagementPage } from './QuizManagementPage';
import type { TrainingModule, Quiz, Question } from '../../types';

export function ModulesManagementPage() {
  const { company } = useCompany();
  const [showForm, setShowForm] = useState(false);
  const [editingModule, setEditingModule] = useState<TrainingModule | null>(null);
  const [modulesList, setModulesList] = useState<TrainingModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [selectedModuleForQuiz, setSelectedModuleForQuiz] = useState<TrainingModule | null>(null);
  const [uploadingThumbnail, setUploadingThumbnail] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    videoUrl: '',
    duration: 0,
    order: 1,
    active: true
  });

  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await modules.list();
      setModulesList(data || []);
    } catch (err: any) {
      console.error('Error loading modules:', err);
      setError(err.message || 'Erro ao carregar módulos. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setError(null);

      // Validate YouTube URL
      const videoId = getYouTubeVideoId(formData.videoUrl);
      if (!videoId) {
        throw new Error('URL do vídeo inválida. Por favor, insira uma URL válida do YouTube.');
      }

      // Convert duration to seconds
      const durationInSeconds = formData.duration * 60;

      const moduleData = {
        ...formData,
        duration: durationInSeconds,
        videoUrl: `https://www.youtube.com/embed/${videoId}`,
        companyId: company.id
      };

      if (editingModule) {
        await modules.update(editingModule.id, moduleData);
      } else {
        await modules.create(moduleData);
      }

      await loadModules();
      setShowForm(false);
      setEditingModule(null);
      setFormData({
        title: '',
        description: '',
        videoUrl: '',
        duration: 0,
        order: modulesList.length + 1,
        active: true
      });
    } catch (err: any) {
      console.error('Error saving module:', err);
      setError(err.message || 'Erro ao salvar módulo. Por favor, tente novamente.');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este módulo?')) return;

    try {
      setError(null);
      await modules.delete(id);
      await loadModules();
    } catch (err: any) {
      console.error('Error deleting module:', err);
      setError(err.message || 'Erro ao excluir módulo. Por favor, tente novamente.');
    }
  };

  const handleThumbnailUpload = async (moduleId: string, file: File) => {
    try {
      setUploadingThumbnail(moduleId);
      setError(null);
      
      // Show preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const preview = e.target?.result as string;
        setModulesList(modules => 
          modules.map(m => 
            m.id === moduleId 
              ? { ...m, thumbnail: preview }
              : m
          )
        );
      };
      reader.readAsDataURL(file);

      // Upload file
      const thumbnailUrl = await modules.uploadThumbnail(moduleId, file);
      
      // Update list with final URL
      setModulesList(modules => 
        modules.map(m => 
          m.id === moduleId 
            ? { ...m, thumbnail: thumbnailUrl }
            : m
        )
      );
    } catch (err: any) {
      console.error('Error uploading thumbnail:', err);
      setError(err.message || 'Erro ao fazer upload da imagem. Por favor, tente novamente.');
    } finally {
      setUploadingThumbnail(null);
    }
  };

  const handleEditQuiz = (module: TrainingModule) => {
    setSelectedModuleForQuiz(module);
    setShowQuiz(true);
  };

  if (showQuiz && selectedModuleForQuiz) {
    return (
      <Layout>
        <QuizManagementPage 
          module={selectedModuleForQuiz}
          onBack={() => {
            setShowQuiz(false);
            setSelectedModuleForQuiz(null);
          }}
          onSave={() => {
            setShowQuiz(false);
            setSelectedModuleForQuiz(null);
            loadModules();
          }}
        />
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Gerenciar Módulos</h1>
            <p className="mt-1 text-gray-500">
              Gerencie os módulos de treinamento e suas avaliações
            </p>
          </div>
          <button
            onClick={() => {
              setEditingModule(null);
              setFormData({
                title: '',
                description: '',
                videoUrl: '',
                duration: 0,
                order: modulesList.length + 1,
                active: true
              });
              setShowForm(true);
            }}
            className={cn(
              "flex items-center gap-2 rounded-lg px-4 py-2 text-white",
              "transition-all hover:opacity-90 active:scale-95 bg-[#7c3aed]"
            )}
          >
            <Plus className="h-4 w-4" />
            Novo Módulo
          </button>
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 p-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {showForm && (
          <div className="rounded-lg bg-white p-6 shadow-sm">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              {editingModule ? 'Editar Módulo' : 'Novo Módulo'}
            </h2>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Título
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className={cn(
                    "mt-1 block w-full rounded-md border border-gray-300 px-3 py-2",
                    "focus:border-purple-500 focus:ring-purple-500"
                  )}
                  placeholder="Ex: Direção Defensiva"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Descrição
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className={cn(
                    "mt-1 block w-full rounded-md border border-gray-300 px-3 py-2",
                    "focus:border-purple-500 focus:ring-purple-500"
                  )}
                  rows={3}
                  placeholder="Descreva o conteúdo do módulo..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  URL do Vídeo (YouTube)
                </label>
                <input
                  type="url"
                  value={formData.videoUrl}
                  onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                  className={cn(
                    "mt-1 block w-full rounded-md border border-gray-300 px-3 py-2",
                    "focus:border-purple-500 focus:ring-purple-500"
                  )}
                  placeholder="https://www.youtube.com/watch?v=..."
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Duração (minutos)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                    className={cn(
                      "mt-1 block w-full rounded-md border border-gray-300 px-3 py-2",
                      "focus:border-purple-500 focus:ring-purple-500"
                    )}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Ordem
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                    className={cn(
                      "mt-1 block w-full rounded-md border border-gray-300 px-3 py-2",
                      "focus:border-purple-500 focus:ring-purple-500"
                    )}
                    required
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <label htmlFor="active" className="text-sm font-medium text-gray-700">
                  Módulo ativo
                </label>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className={cn(
                    "px-4 py-2 text-gray-700 border border-gray-300 rounded-lg",
                    "transition-all hover:bg-gray-50 active:scale-95"
                  )}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className={cn(
                    "px-4 py-2 text-white rounded-lg",
                    "transition-all hover:opacity-90 active:scale-95 bg-[#7c3aed]"
                  )}
                >
                  {editingModule ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden bg-white shadow-sm rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Módulo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duração
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ordem
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {modulesList.map((module) => (
                    <tr key={module.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <div className="relative h-16 w-24 rounded-lg overflow-hidden bg-gray-100">
                            {uploadingThumbnail === module.id ? (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                              </div>
                            ) : module.thumbnail ? (
                              <img
                                src={module.thumbnail}
                                alt={module.title}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <Image className="h-6 w-6 text-gray-400" />
                              </div>
                            )}
                            <label className={cn(
                              "absolute inset-0 flex items-center justify-center",
                              "bg-black/60 opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                            )}>
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleThumbnailUpload(module.id, file);
                                  }
                                }}
                              />
                              <Image className="h-5 w-5 text-white" />
                            </label>
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {module.title}
                            </div>
                            <div className="text-sm text-gray-500 line-clamp-1">
                              {module.description}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {module.active ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3" />
                            Ativo
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <XCircle className="h-3 w-3" />
                            Inativo
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {Math.floor(module.duration / 60)}min
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {module.order}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEditQuiz(module)}
                            className={cn(
                              "text-purple-600 hover:text-purple-900",
                              "transition-colors p-1 rounded-lg hover:bg-purple-50"
                            )}
                            title="Editar Questionário"
                          >
                            <FileQuestion className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setEditingModule(module);
                              setFormData({
                                title: module.title,
                                description: module.description,
                                videoUrl: module.videoUrl,
                                duration: Math.floor(module.duration / 60),
                                order: module.order,
                                active: module.active
                              });
                              setShowForm(true);
                            }}
                            className={cn(
                              "text-blue-600 hover:text-blue-900",
                              "transition-colors p-1 rounded-lg hover:bg-blue-50"
                            )}
                            title="Editar Módulo"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleDelete(module.id)}
                            className={cn(
                              "text-red-600 hover:text-red-900",
                              "transition-colors p-1 rounded-lg hover:bg-red-50"
                            )}
                            title="Excluir Módulo"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function getYouTubeVideoId(url: string) {
  return url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})(?:\S+)?/)?.[1];
}